import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PlugZap } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { UTILITY_STATUS, fmtDate, fmtEUR } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { ActionButtons, useProjectPicker } from "../../components/ops";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const UTILITY_TYPES = {
  electricidad: "Electricidad", agua: "Agua", gas: "Gas",
  telecomunicaciones: "Telecomunicaciones", saneamiento: "Saneamiento", otras: "Otras",
};

const STAGES = ["solicitud", "oferta", "aprobada", "ejecucion", "documentacion", "prueba", "conexion", "en_servicio"];

export default function Acometidas() {
  const [pid, picker] = useProjectPicker();
  const users = useUsers();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ utility_type: "electricidad" });
  const { data: items } = useQuery({
    queryKey: ["ops-utilities", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/acometidas`)).data,
  });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/acometidas`, f)).data,
    onSuccess: () => { toast.success("Acometida solicitada"); setOpen(false); setF({ utility_type: "electricidad" }); qc.invalidateQueries({ queryKey: ["ops-utilities", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  return (
    <div className="space-y-4" data-testid="acometidas-page">
      <PageHeader title="Acometidas"
        subtitle="Solicitud → oferta → aprobación → ejecución → documentación → prueba → conexión → puesta en servicio. Sin prueba/evidencia no hay «En servicio»."
        actions={<div className="flex items-center gap-2">{picker}
          <Button size="sm" onClick={() => setOpen(true)} data-testid="create-utility-open">Nueva acometida</Button></div>}
        testid="acometidas-title" />
      {!items?.length ? <EmptyState title="Sin acometidas" description="Solicita la primera acometida del proyecto." /> : (
        <Table data-testid="acometidas-table">
          <TableHeader><TableRow>
            <TableHead>Acometida</TableHead><TableHead>Progreso</TableHead><TableHead>Suministrador</TableHead>
            <TableHead>Oferta</TableHead><TableHead>Prueba</TableHead><TableHead>Puesta en servicio</TableHead>
            <TableHead>Responsable</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((u) => {
              const idx = STAGES.indexOf(u.status);
              return (
                <TableRow key={u.id} data-testid={`utility-row-${u.id}`}>
                  <TableCell className="max-w-56"><p className="font-medium text-slate-800">{u.title}</p>
                    <p className="text-xs text-slate-400">{UTILITY_TYPES[u.utility_type]}</p></TableCell>
                  <TableCell data-testid={`utility-progress-${u.id}`}>
                    <div className="flex items-center gap-1">
                      {STAGES.map((s, i) => (
                        <div key={s} title={UTILITY_STATUS[s]?.label}
                          className={`h-2 w-4 rounded-sm ${i <= idx ? (u.status === "en_servicio" ? "bg-emerald-500" : "bg-blue-600") : "bg-slate-200"}`} />
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-slate-500"><Chip map={UTILITY_STATUS} value={u.status} testid={`utility-status-${u.id}`} /></p>
                  </TableCell>
                  <TableCell className="text-xs">{u.supplier || "—"}</TableCell>
                  <TableCell className="text-xs">{u.offer_amount ? fmtEUR(u.offer_amount) : "—"}</TableCell>
                  <TableCell className="max-w-40 truncate text-xs text-slate-500" title={u.test_evidence || ""}>{u.test_evidence || "—"}</TableCell>
                  <TableCell className="text-xs">{u.in_service_at ? fmtDate(u.in_service_at) : "—"}</TableCell>
                  <TableCell className="text-xs">{userName(users.map, u.responsible_id)}</TableCell>
                  <TableCell><ActionButtons item={u} endpoint={`/proyectos/${pid}/ops/acometidas`} invalidate={[["ops-utilities", pid]]} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-utility-dialog">
          <DialogHeader><DialogTitle>Nueva acometida</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo *</Label>
              <Select value={f.utility_type || "electricidad"} onValueChange={(v) => setF({ ...f, utility_type: v })}>
                <SelectTrigger data-testid="create-utility-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(UTILITY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Título</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={UTILITY_TYPES[f.utility_type || "electricidad"]} data-testid="create-utility-title" /></div>
            <Button data-testid="create-utility-submit" disabled={mut.isPending} onClick={() => mut.mutate()}>Solicitar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
